import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, 
  FaUniversity, 
  FaCloud, 
  FaShieldAlt,
  FaHospital,
  FaSchool,
  FaUsers,
  FaMobileAlt,
  FaPlug,
  FaMoneyBillWave,
  FaRobot,
  FaStar,
  FaGem
} from 'react-icons/fa';
import heroImage from '@assets/micropoints.png';

const fallbackImage = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&h=900&fit=crop&crop=center';

// ─── Constants ────────────────────────────────────────────────────

const SLIDES = [
  {
    id: 1,
    title: 'Core Banking Systems',
    description: 'Enterprise-grade core banking platform for financial institutions of all sizes. Member management, loan processing, and savings accounts.',
    link: '/products/core-banking',
    cta: 'Explore Core Banking',
    icon: FaUniversity,
    gradient: 'from-blue-500 to-indigo-600'
  },
  {
    id: 2,
    title: 'Microsoft Dynamics 365 ERP',
    description: 'Enterprise resource planning solutions including implementation, support, and customization for financials, supply chain, and operations.',
    link: '/products/erp-solutions',
    cta: 'Explore ERP Solutions',
    icon: FaCloud,
    gradient: 'from-blue-600 to-cyan-600'
  },
  {
    id: 3,
    title: 'Hospital Management Systems',
    description: 'Comprehensive healthcare management solutions including EMR, patient records, billing, and administrative tools for modern hospitals.',
    link: '/products/hospital-management',
    cta: 'Explore Healthcare',
    icon: FaHospital,
    gradient: 'from-blue-500 to-teal-600'
  },
  {
    id: 4,
    title: 'School Management Systems',
    description: 'Complete education management platform for student records, examinations, fee processing, and academic administration.',
    link: '/products/school-management',
    cta: 'Explore Education',
    icon: FaSchool,
    gradient: 'from-indigo-500 to-blue-600'
  },
  {
    id: 5,
    title: 'HR & Payroll Solutions',
    description: 'Human resources management with payroll processing, leave management, claims handling, and employee records management.',
    link: '/products/hr-payroll',
    cta: 'Explore HR Solutions',
    icon: FaUsers,
    gradient: 'from-purple-500 to-indigo-600'
  },
  {
    id: 6,
    title: 'Mobile Banking Platforms',
    description: 'Mobile banking solutions including USSD, mobile applications, and agency banking for financial inclusion and accessibility.',
    link: '/products/mobile-banking',
    cta: 'Explore Mobile Banking',
    icon: FaMobileAlt,
    gradient: 'from-cyan-500 to-blue-600'
  },
  {
    id: 7,
    title: 'Bank Integration Services',
    description: 'API gateway solutions, ISO8583 switching, ATM integration, and payment processing for seamless financial connectivity.',
    link: '/products/payment-integration',
    cta: 'Explore Integrations',
    icon: FaPlug,
    gradient: 'from-blue-500 to-indigo-700'
  },
  {
    id: 8,
    title: 'Mobile Money Solutions',
    description: 'M-PESA integration, B2C and C2B payments, mobile money platforms, and digital payment systems for African markets.',
    link: '/products/payment-integration',
    cta: 'Explore Mobile Money',
    icon: FaMoneyBillWave,
    gradient: 'from-emerald-500 to-blue-600'
  },
  {
    id: 9,
    title: 'AI & Training Services',
    description: 'Machine learning solutions, automation tools, analytics platforms, and training programs for digital innovation.',
    link: '/products/ai-training',
    cta: 'Explore AI Solutions',
    icon: FaRobot,
    gradient: 'from-slate-600 to-blue-600'
  }
];

const TRUST_INDICATORS = [
  { icon: FaUniversity, label: 'SACCO & Banking' },
  { icon: FaCloud, label: 'Microsoft Partner' },
  { icon: FaShieldAlt, label: 'Enterprise Security' },
];

// ─── Subcomponents ────────────────────────────────────────────────

const TrustBadge = () => (
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600/30 to-indigo-600/30 backdrop-blur-sm px-3 py-1 md:px-4 md:py-1.5 rounded-full border border-blue-400/30 mb-3 md:mb-4 shadow-lg shadow-blue-500/10">
    <span className="relative flex h-1.5 w-1.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
    </span>
    <span className="text-white text-[10px] md:text-xs font-medium">
      Trusted by 150+ Organizations
    </span>
    <div className="flex gap-0.5">
      <FaStar className="text-yellow-400 text-[8px] md:text-[10px]" />
      <FaStar className="text-yellow-400 text-[8px] md:text-[10px]" />
      <FaStar className="text-yellow-400 text-[8px] md:text-[10px]" />
      <FaStar className="text-yellow-400 text-[8px] md:text-[10px]" />
      <FaStar className="text-yellow-400/50 text-[8px] md:text-[10px]" />
    </div>
  </div>
);

const TrustIndicators = () => (
  <div className="flex flex-wrap gap-3 md:gap-4 mt-4 md:mt-5 pt-3 md:pt-4 border-t border-white/15">
    {TRUST_INDICATORS.map((item, i) => {
      const Icon = item.icon;
      return (
        <span key={i} className="flex items-center gap-1.5 text-white/80 text-[10px] md:text-xs drop-shadow-sm hover:text-white transition-colors">
          <div className="p-1 rounded-lg bg-blue-500/20">
            <Icon className="text-blue-300 text-[10px] md:text-xs" />
          </div>
          {item.label}
        </span>
      );
    })}
  </div>
);

const SlideContent = ({ slide, currentSlide }) => {
  const Icon = slide.icon;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentSlide}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className="text-center"
      >
        {/* Icon with glow */}
        <div className="relative inline-block">
          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${slide.gradient} blur-xl opacity-60 animate-pulse`} />
          <div className={`relative inline-flex w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${slide.gradient} items-center justify-center mb-3 md:mb-4 shadow-lg shadow-blue-500/20`}>
            <Icon className="text-2xl md:text-3xl text-white" />
          </div>
        </div>
        
        <h3 className="text-base md:text-xl font-bold text-white mb-1.5 md:mb-2 drop-shadow-lg bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
          {slide.title}
        </h3>
        
        <p className="text-white/90 text-xs md:text-sm mb-3 md:mb-4 max-w-sm mx-auto leading-relaxed drop-shadow-md">
          {slide.description}
        </p>
        
        <Link to={slide.link}>
          <button className="group bg-[#E8960D] hover:bg-[#d4810a] text-white px-5 md:px-6 py-1.5 md:py-2 rounded-full font-semibold text-xs md:text-sm transition-all border border-[#E8960D]/30 hover:border-[#E8960D]/50 inline-flex items-center gap-1.5 hover:gap-2 shadow-lg shadow-[#E8960D]/20 hover:shadow-[#E8960D]/30">
            {slide.cta}
            <FaArrowRight className="text-[10px] md:text-xs group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
};

const SlideNavigation = ({ slides, currentSlide, goToSlide, prevSlide, nextSlide }) => (
  <div className="flex items-center justify-between mt-3 md:mt-4 pt-3 md:pt-4 border-t border-white/15">
    {/* Dot Indicators */}
    <div className="flex gap-1.5 md:gap-2">
      {slides.map((_, index) => (
        <button
          key={index}
          onClick={() => goToSlide(index)}
          className={`transition-all duration-300 rounded-full ${
            currentSlide === index
              ? 'w-6 md:w-8 h-1.5 bg-gradient-to-r from-blue-400 to-indigo-400 shadow-lg shadow-blue-400/50'
              : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'
          }`}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
    
    {/* Arrow Navigation */}
    <div className="flex gap-1">
      <button
        onClick={prevSlide}
        className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white hover:text-white transition-all flex items-center justify-center border border-white/20 hover:border-white/40 hover:scale-105"
        aria-label="Previous slide"
      >
        <svg className="w-3 h-3 md:w-3.5 md:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white hover:text-white transition-all flex items-center justify-center border border-white/20 hover:border-white/40 hover:scale-105"
        aria-label="Next slide"
      >
        <svg className="w-3 h-3 md:w-3.5 md:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || SLIDES.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    if (index < 0 || index >= SLIDES.length) return;
    setIsAutoPlaying(false);
    setCurrentSlide(index);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  const nextSlide = () => {
    if (SLIDES.length === 0) return;
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  const prevSlide = () => {
    if (SLIDES.length === 0) return;
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  // Preload image
  useEffect(() => {
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      console.warn('Local image failed to load, using fallback');
      setImageLoaded(true);
    };
  }, []);

  // Safety check
  if (SLIDES.length === 0 || currentSlide >= SLIDES.length) {
    return (
      <section className="relative overflow-hidden min-h-[60vh] md:min-h-[80vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
          style={{ backgroundImage: `url(${heroImage || fallbackImage})` }}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
        <div className="relative container mx-auto px-4 max-w-7xl z-10">
          <div className="text-white text-center">
            <h1 className="text-4xl font-bold">Loading...</h1>
          </div>
        </div>
      </section>
    );
  }

  const currentSlideData = SLIDES[currentSlide];
  const backgroundImage = imageLoaded ? heroImage : fallbackImage;

  return (
    <section className="relative overflow-hidden min-h-[60vh] md:min-h-[80vh] flex items-center">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
        <div className="absolute inset-0 bg-blue-600/10 mix-blend-overlay" />
      </div>
      
      <div className="relative container mx-auto px-4 max-w-7xl z-10 py-6 md:py-12">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <TrustBadge />

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight drop-shadow-2xl">
              Powering Financial &amp;
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-200 to-cyan-200">
                Enterprise Solutions
              </span>
            </h1>

            <p className="text-white/90 text-sm md:text-base lg:text-lg mt-3 md:mt-4 max-w-xl leading-relaxed drop-shadow-lg">
              Core Banking Systems and Microsoft Dynamics 365 ERP solutions 
              for organizations across Africa.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-2 md:gap-3 mt-5 md:mt-6">
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 bg-[#E8960D] hover:bg-[#d4810a] text-white px-6 md:px-8 py-2 md:py-2.5 rounded-full font-semibold text-sm md:text-base transition-all shadow-lg hover:shadow-xl hover:shadow-[#E8960D]/30 hover:-translate-y-0.5"
              >
                <span>Request Demo</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white px-6 md:px-8 py-2 md:py-2.5 rounded-full font-semibold text-sm md:text-base transition-all border border-white/25 hover:border-white/50 hover:-translate-y-0.5"
              >
                Explore Products
              </Link>
            </div>

            <TrustIndicators />
          </motion.div>

          {/* Right Column - Slider */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            className="relative mt-4 md:mt-0"
          >
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 shadow-2xl shadow-black/20">
              
              {/* Slide Header */}
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <div className="flex items-center gap-2">
                  <FaGem className="text-blue-400 text-[10px] md:text-xs" />
                  <span className="text-white/70 text-[8px] md:text-[10px] font-bold tracking-widest uppercase">
                    Featured Solutions
                  </span>
                </div>
                <span className="text-white/50 text-[10px] md:text-xs font-medium bg-white/10 px-2.5 py-0.5 rounded-full border border-white/10">
                  {String(currentSlide + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
                </span>
              </div>
              
              {/* Slide Content */}
              <SlideContent 
                slide={currentSlideData} 
                currentSlide={currentSlide} 
              />

              {/* Navigation */}
              <SlideNavigation 
                slides={SLIDES}
                currentSlide={currentSlide}
                goToSlide={goToSlide}
                prevSlide={prevSlide}
                nextSlide={nextSlide}
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}